import {
  CalendarClock,
  Clock,
  Mail,
  MessageSquare,
  NotebookText,
  Plug,
  Puzzle,
  Send,
  Sparkles,
  Wallet,
  Wrench,
  type LucideIcon,
} from "lucide-react"
import type { ReactNode } from "react"
import { useNavigate } from "react-router-dom"

import { RichText } from "@/components/rich-text"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useT } from "@/i18n/provider"

/**
 * A plain-language guide for newcomers: what the platform is, what agents can do, and the
 * two things people most often get stuck on — creating an agent and connecting a Telegram
 * bot. Written for anyone, not just developers. Static content; it links out to the real
 * pages where the work happens. All copy lives in the i18n catalogs under `guide.*`;
 * inline emphasis uses RichText markers.
 */

const USE_CASES: { icon: LucideIcon; key: string }[] = [
  { icon: Wallet, key: "spending" },
  { icon: Mail, key: "email" },
  { icon: CalendarClock, key: "calendar" },
  { icon: Clock, key: "briefings" },
  { icon: Wrench, key: "tools" },
  { icon: Send, key: "telegram" },
]

const STEPS = [1, 2, 3, 4, 5] as const

/** A numbered instruction with an optional inline highlight. */
function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="bg-primary text-primary-foreground mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
        {n}
      </span>
      <span className="text-sm leading-relaxed">{children}</span>
    </li>
  )
}

function SectionHeading({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <h2 className="flex items-center gap-2 text-lg font-semibold">
      <Icon className="text-muted-foreground size-5" />
      {children}
    </h2>
  )
}

export function GuidePage() {
  const navigate = useNavigate()
  const t = useT()

  return (
    <div className="mx-auto max-w-3xl space-y-12 p-6">
      <header className="space-y-3">
        <h1 className="text-2xl font-bold tracking-tight">{t("guide.title")}</h1>
        <p className="text-muted-foreground leading-relaxed">
          <RichText text={t("guide.intro")} />
        </p>
      </header>

      <section className="space-y-4">
        <SectionHeading icon={Sparkles}>{t("guide.useCases.title")}</SectionHeading>
        <p className="text-muted-foreground text-sm">{t("guide.useCases.intro")}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {USE_CASES.map(({ icon: Icon, key }) => (
            <Card key={key}>
              <CardContent className="flex gap-3 p-4">
                <span className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-lg">
                  <Icon className="size-5" />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium">
                    {t(`guide.useCases.items.${key}.title`)}
                  </div>
                  <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
                    {t(`guide.useCases.items.${key}.blurb`)}
                  </p>
                  <p className="text-muted-foreground/80 mt-1 text-xs">
                    {t("guide.useCases.usesLabel", {
                      name: t(`guide.useCases.items.${key}.using`),
                    })}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeading icon={MessageSquare}>{t("guide.createAgent.title")}</SectionHeading>
        <p className="text-muted-foreground text-sm">{t("guide.createAgent.intro")}</p>
        <ol className="space-y-3">
          {STEPS.map((n) => (
            <Step key={n} n={n}>
              <RichText text={t(`guide.createAgent.step${n}`)} />
            </Step>
          ))}
        </ol>
        <Button onClick={() => navigate("/agents/new")}>{t("guide.createAgent.cta")}</Button>
      </section>

      <section className="space-y-4">
        <SectionHeading icon={Puzzle}>{t("guide.tools.title")}</SectionHeading>
        <p className="text-muted-foreground text-sm leading-relaxed">
          <RichText text={t("guide.tools.body")} />
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate("/plugins")}>
            <Puzzle className="size-4" />
            {t("guide.tools.browsePlugins")}
          </Button>
          <Button variant="outline" onClick={() => navigate("/connections")}>
            <Plug className="size-4" />
            {t("guide.tools.connectServer")}
          </Button>
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeading icon={NotebookText}>{t("guide.notion.title")}</SectionHeading>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {t("guide.notion.intro")}
        </p>
        <ol className="space-y-3">
          {STEPS.map((n) => (
            <Step key={n} n={n}>
              <RichText text={t(`guide.notion.step${n}`)} />
            </Step>
          ))}
        </ol>
        <Button variant="outline" onClick={() => navigate("/agents")}>
          {t("guide.notion.openAgents")}
        </Button>
      </section>

      <section className="space-y-4">
        <SectionHeading icon={Send}>{t("guide.telegram.title")}</SectionHeading>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {t("guide.telegram.intro")}
        </p>
        <ol className="space-y-3">
          {STEPS.map((n) => (
            <Step key={n} n={n}>
              <RichText text={t(`guide.telegram.step${n}`)} />
            </Step>
          ))}
        </ol>
        <Button variant="outline" onClick={() => navigate("/agents")}>
          {t("guide.telegram.openAgents")}
        </Button>
      </section>

      <section className="space-y-4">
        <SectionHeading icon={Clock}>{t("guide.schedule.title")}</SectionHeading>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {t("guide.schedule.body")}
        </p>
        <Button variant="outline" onClick={() => navigate("/schedules")}>
          <CalendarClock className="size-4" />
          {t("guide.schedule.cta")}
        </Button>
      </section>
    </div>
  )
}
